import * as React from 'react';
import TableBody from '@mui/material/TableBody';
import { getComparator, stableSort } from '../../../utils/TableSortUtils';
import { GeneralTableToolbar } from './components/GeneralTableToolbar';
import {
    HeadCell,
    Order,
} from '../../../models/TableProps';
import { TableHeadWrapper } from './components/TableHeadWrapper';
import { TableWrapper } from './components/TableWrapper';
import { TableContentWrapper } from './components/TableContentWrapper';
import { TableEmptyRows } from './components/TableEmptyRows';
import { TableLoadingLayout } from '../../layouts/LoadingLayouts/TableLoadingLayout';
import { useEffect } from 'react';

export const GeneralTable = <T, S>(props: QuotationsProps<T, S>) => {
    const {
        fullList,
        rowComponent,
        filterTerm,
        defaultOrderBy,
        defaultOrder,
        selectHandleId,
        tableHeadSettings,
        tableToolbarSettings,
        forceRefresh,
    } = props;
    const [order, setOrder] = React.useState<Order>(defaultOrder ? defaultOrder : 'asc');
    const [orderBy, setOrderBy] = React.useState<keyof S>(defaultOrderBy);
    const [selected, setSelected] = React.useState([]);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const [rows, setRows] = React.useState(fullList);

    useEffect(() => {
        setRows(fullList);
    }, [fullList]);

    const handleRequestSort = (event: React.MouseEvent<unknown>, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSelectAllClick = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        if (event.target.checked) {
            const newSelected = rows.map((n) => n[selectHandleId]);
            setSelected(newSelected);
            return;
        }
        setSelected([]);
    };

    const handleClick = (event: React.MouseEvent<unknown>, name: string) => {
        const selectedIndex = selected.indexOf(name);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, name);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1)
            );
        }

        setSelected(newSelected);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleFilter = (search) => {
        setPage(0);
        if (search.length > 0) {
            setRows(filterTerm(search));
        } else {
            setRows(fullList);
        }
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleForceRefresh = () => {
        forceRefresh();
    };

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

    return (
        <TableWrapper
            rows={rows}
            paginationHandle={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
        >
            <GeneralTableToolbar
                numSelected={selected.length}
                onFilter={handleFilter}
                tableToolbarSettings={tableToolbarSettings}
            />
            <TableContentWrapper>
                <TableHeadWrapper<S>
                    selectable={tableHeadSettings.selectable}
                    onSelectAllClick={handleSelectAllClick}
                    order={order}
                    orderBy={orderBy}
                    numSelected={selected.length}
                    rowCount={rows.length}
                    onRequestSort={handleRequestSort}
                    headContent={tableHeadSettings.headContent}
                    translateNs={tableHeadSettings.translateNs}
                    translatePrefix={tableHeadSettings.translatePrefix}
                />
                <TableBody>
                    {stableSort<any>(rows, getComparator(order, orderBy))
                        .slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage
                        )
                        .map((row, index) => {
                            return React.createElement(rowComponent, {
                                key: index,
                                row: row,
                                index: index,
                                clickHandle: handleClick,
                                selected: selected,
                            });
                        })}
                    <TableEmptyRows
                        emptyRows={emptyRows}
                        dense={false}
                        colSpan={10}
                    />
                </TableBody>
            </TableContentWrapper>
        </TableWrapper>
    );
};

interface QuotationsProps<T, S> {
    fullList: T[];
    rowComponent: any;
    filterTerm(search: string): T[];
    defaultOrderBy: keyof S;
    defaultOrder?: Order;
    selectHandleId: keyof T;
    tableHeadSettings: {
        headContent: HeadCell<S>[];
        translateNs: string;
        translatePrefix: string;
        selectable: boolean;
    };
    tableToolbarSettings: {
        translateNs: string;
        translatePrefix: string;
    };
    forceRefresh?: () => void;
    isLoading: boolean;
}
